/**
 * hubPageIdentity.ts — the 課務總覽 page identity table (style sheet § 2, text-fix § A1; Hub UI
 * W0.1): each page's colour family, icon, name, one-sentence purpose, hover and identity-line
 * link. Moved as-is out of `config/hubUi.ts` (2026-10-14, 續報・轉班 完成規則 W8.3 — hubUi.ts was
 * over the .ts hard limit); no value changed.
 *
 * Re-exported by `config/appConfig.ts` (`export * from './hubPageIdentity'`, beside hubUi's), so
 * every importer keeps `@/config/appConfig`. NOT re-exported by hubUi.ts: the sentences read
 * hubUi's button labels, so a re-export there would make the two files import each other. Like
 * hubUi.ts it imports NOTHING from appConfig (appConfig re-exports this module — a value read at
 * module load would hit the circular-import TDZ); sentences with a business number are
 * functions of a `HubTextConfig` the caller builds (utils/hubIdentity.ts).
 */
import {
  HUB_CHANGE_TARGET_LABEL,
  HUB_MUTATING_ACTIONS,
  HUB_OPEN_ACTIONS,
  HUB_TRANSFER_HANDLE_LABEL,
  type HubFamily,
  type HubIcon,
  type HubText,
} from './hubUi';

export type HubPageKey =
  | 'lsedReview'
  | 'renewalPast'
  | 'renewalPending'
  | 'renewalAwaiting'
  | 'renewalNotify'
  | 'renewalMissed'
  | 'renewalHold'
  | 'transferInbox'
  | 'transferArranging'
  | 'transferAwaiting'
  | 'transferDone'
  | 'transferExpired'
  | 'waitlist'
  | 'studentRecord'
  | 'quickLinks'
  | 'bfInbox';

/**
 * The identity line's right-hand link. `explainer` / `help` open a disclosure panel
 * (a real button with aria-expanded); `back` leaves the tool view.
 */
export interface HubPageLink {
  label: string;
  kind: 'explainer' | 'help' | 'back';
  icon?: HubIcon;
}

export interface HubPageIdentity {
  family: HubFamily;
  icon: HubIcon;
  /** Page name — the bold word on the identity line and the sub-tab label. */
  title: string;
  /** One-sentence purpose (text-fix § A1). Numbers only through a function of config. */
  sentence: HubText;
  /** Hover title of the identity line (the threshold sentences, D-11). */
  hoverTitle?: HubText;
  link?: HubPageLink;
}

/**
 * The identity line's ⓘ on phones (PO 2026-09-29 option A): the purpose sentence folds
 * behind it (aria-expanded; toggles the sentence inline). Desktop shows the sentence and
 * no button.
 */
export const HUB_IDENT_INFO: { icon: HubIcon; label: string } = { icon: 'info', label: '說明' };

const RENEWAL_EXPLAINER_LINK: HubPageLink = {
  label: '期限怎麼算？',
  kind: 'explainer',
  icon: 'schedule',
};
const HELP_LINK: HubPageLink = { label: '使用說明', kind: 'help' };
const BACK_LINK: HubPageLink = { label: '返回總覽', kind: 'back', icon: 'arrow_back' };

export const HUB_PAGE_IDENTITY: Record<HubPageKey, HubPageIdentity> = {
  lsedReview: {
    family: 'rose',
    icon: 'fact_check',
    title: '待確認',
    // 學期票券 (PO 2026-10-02, review-queue proposal § 1): only 學期 tickets count.
    sentence: 'Excel 上出現在最後約課日之後、又沒有達到自動續報門檻的學期票券約課，請判斷',
    hoverTitle: (cfg) =>
      `最後約課日之後有 ${cfg.lsedReviewMinCount} 到 ${cfg.autoTagThreshold - 1} 堂學期票券約課才會列入 (達 ${cfg.autoTagThreshold} 堂自動續報)`,
    link: HELP_LINK,
  },
  renewalPast: {
    family: 'red',
    icon: 'error',
    title: '已過期未通知',
    // Review H-11: the two buttons by their labels (hubUi.ts HUB_BUTTON_KIND — a function,
    // so they are read when the sentence is resolved, not at module load).
    // PO 2026-09-29 (期壬, option A): the parenthesis — a seat still under a live lock is
    // released only when the lock passes (the row says 名額保留至 M/D until then).
    sentence: () =>
      `已過續報通知期限、且從未通知過的學生；名額已釋出 (仍在保留期的，保留到期後才釋出)，請選擇 ${HUB_OPEN_ACTIONS.extendAndNotify.label} 或 ${HUB_MUTATING_ACTIONS.releaseSeatConfirmed.label} · 學期結束最近的在前`,
    link: RENEWAL_EXPLAINER_LINK,
  },
  renewalPending: {
    family: 'yellow',
    icon: 'notifications',
    title: '待通知',
    sentence: '進入續報通知窗口、課務尚未通知家長的學生 · 無約課的先確認，其餘依剩餘天數',
    link: RENEWAL_EXPLAINER_LINK,
  },
  renewalAwaiting: {
    family: 'orange',
    icon: 'hourglass_empty',
    title: '已通知·待確認',
    sentence: '已通知續報、家長尚未繳款或 Excel 還沒上傳新預約資料',
    link: RENEWAL_EXPLAINER_LINK,
  },
  renewalNotify: {
    family: 'blue',
    icon: 'mail',
    title: '約課通知待發',
    sentence: '系統已偵測續報成功，請送出真實日期的約課成功通知並標記完成 · 等最久的在前',
    link: RENEWAL_EXPLAINER_LINK,
  },
  renewalMissed: {
    family: 'slate',
    icon: 'folder',
    title: '近期未處理',
    sentence: '近期已處理但未結案的續報紀錄',
    link: RENEWAL_EXPLAINER_LINK,
  },
  renewalHold: {
    family: 'purple',
    icon: 'lock',
    title: '暫時保留名額',
    sentence: '課務手動標記為暫時保留的名額 (永久持有，不會自動結算) · 保留最久的在最上面',
    link: RENEWAL_EXPLAINER_LINK,
  },
  /** W7.2 (J-01): the first 轉班 stage (was the 新建議 panel); cyan = the teacher family. */
  transferInbox: {
    family: 'cyan',
    icon: 'inbox',
    title: '老師建議',
    sentence: () =>
      `老師提出的轉班建議，由課務 ${HUB_OPEN_ACTIONS.handleSuggestion.label}：${HUB_TRANSFER_HANDLE_LABEL.registerTransfer}、${HUB_TRANSFER_HANDLE_LABEL.toWaitlist}、${HUB_TRANSFER_HANDLE_LABEL.handledElsewhere} 或 ${HUB_TRANSFER_HANDLE_LABEL.reject}；處理前不會自己消失 · 緊急件在前，其餘依建議先後`,
    link: HELP_LINK,
  },
  transferArranging: {
    family: 'yellow',
    icon: 'sync',
    title: '安排中',
    sentence: '課務已接手、尚未確定轉入的轉班 · 緊急件在前，其餘依登記先後 (最久的在前)',
    hoverTitle: (cfg) =>
      `超過 ${cfg.transferAging.standard.amber} 天未處理轉黃、${cfg.transferAging.standard.red} 天轉紅 (緊急件 ${cfg.transferAging.urgent.amber} / ${cfg.transferAging.urgent.red} 天)`,
    // 使用說明 (PO 2026-10-01, M1): the four stages share ONE explainer and one open state
    // (TransferRequestsTab → TransferExplainer).
    link: HELP_LINK,
  },
  transferAwaiting: {
    family: 'blue',
    icon: 'hourglass_empty',
    title: '待約課',
    // PO 2026-10-02 (D-1 / D-2): a row whose new class still has bookings ahead does not
    // expire (functions maintenance skips it) — the bookings may be this term or 補課, so
    // the sentence names both ways (was 「已約 1–7 堂的不會到期，請按「結算轉班」」); 補課's
    // three exits as every other list names them (fix round 1, UX LOW 16).
    sentence: (cfg) =>
      `已確定轉入，等待 BF Excel 約課資料 · 保留 ${cfg.transferBfGraceDays} 天，最後 ${cfg.transferBfWarningDays} 天轉紅；到期還沒有新班約課 → 未完成 (新班還有之後的約課時不會到期：家長確認是這期學期 → ${HUB_OPEN_ACTIONS.settleTransfer.label}；可能是補課 → ${HUB_OPEN_ACTIONS.otherHandling.ariaLabel}：${HUB_MUTATING_ACTIONS.cancelTransfer.label}、${HUB_OPEN_ACTIONS.convertToWaitlist.label} 或 ${HUB_CHANGE_TARGET_LABEL})`,
    link: HELP_LINK,
  },
  transferDone: {
    family: 'green',
    icon: 'check_circle',
    title: '已完成',
    sentence: (cfg) =>
      `已收到 BF 約課資料 · ${cfg.transferCompletedRetentionDays} 天後自動移出清單，學生紀錄仍可查閱`,
    link: HELP_LINK,
  },
  transferExpired: {
    family: 'red',
    icon: 'warning',
    title: '未完成',
    // PO 2026-10-03 (未完成 自動歸檔): rows close by themselves — the same words as 已完成's.
    sentence: (cfg) =>
      `保留到期仍未收到約課資料，需重新處理 · 到期 ${cfg.transferExpiredAutoCloseDays} 天後自動歸檔 (新班已保留名額的隔天凌晨)，學生紀錄仍可查閱`,
    link: HELP_LINK,
  },
  waitlist: {
    family: 'green',
    icon: 'format_list_numbered',
    title: '候補',
    sentence: '先登記先排；有名額可上的家庭放在上面',
    link: HELP_LINK,
  },
  studentRecord: {
    family: 'blue',
    icon: 'person',
    title: '學生紀錄',
    sentence: '查一位學生的約課時段、課程進度、學期紀錄、學習歷程',
    link: BACK_LINK,
  },
  // Derived entries (review C2): the same tool-view bar as 學生紀錄, theme blue, no
  // purpose sentence of their own yet (no approved copy — title only).
  quickLinks: { family: 'blue', icon: 'link', title: '快速連結', sentence: '', link: BACK_LINK },
  bfInbox: { family: 'blue', icon: 'mail', title: '未上傳約課', sentence: '', link: BACK_LINK },
};
